import React, { useMemo, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";

import ERRORS from "common/errors";
import stringEncryptRSA from "common/string-encrypt-rsa";
import api from "services/api";
import pathnames from "routes/pathnames";
import serveRequestErrors from "common/serve-request-errors";
import AppAlert from "components/app-alert";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import logo from "assets/images/logo.webp";
import activateIcon from "assets/images/activate-icon.svg";
import exclamationIcon from "assets/images/exclamation-icon.svg";
import AppSuccessfulModal from "components/pages/dashboard/app-successful-modal";

const PageResetPassword = (props) => {
    const location = useLocation();
    const { otpCode, email } = location.state || {};
    const successModalRef = useRef();
    const navigate = useNavigate();
    const formik = useFormik({
        initialValues: {
            password: "",
            confirmPassword: "",
        },
        validationSchema: yup.object({
            password: yup.string().required(ERRORS.REQUIRED).test("no-space", ERRORS.SPACE, (value) => !/\s/.test(value || "")),
            confirmPassword: yup.string().required(ERRORS.REQUIRED).test("no-space", ERRORS.SPACE, (value) => !/\s/.test(value || "")),
        }),
        onSubmit: (values) => {
            onHandleSubmit(values);
        },
    });

    const rules = useMemo(() => [
        {
          label: "Use at least 8 characters",
          isValid: formik.values.password.length >= 8,
        },
        {
          label: "Should contain at least 1 uppercase",
          isValid: /[A-Z]/.test(formik.values.password),
        },
        {
          label: "Should contain at least 1 numeric",
          isValid: /[0-9]/.test(formik.values.password),
        },
        {
          label: "Should contain at least 1 special character",
          isValid: /[!@#$%^&*,.?]/.test(formik.values.password),
        },
      ], [formik.values.password]);

    const isFormValid = useMemo(() => {
        const allRulesPassed = rules.every(rule => rule.isValid);
        return allRulesPassed && formik.values.password === formik.values.confirmPassword;
      }, [rules, formik.values.password, formik.values.confirmPassword]);

    const onHandleSubmit = async (values) => {
        try {
            const payload = {
                newPassword: stringEncryptRSA(values.password),
                email: email,
                otpCode: otpCode,
            };

            await api.post.general.resetPassword(payload);
            onHandleSuccessModal();

        } catch (error) {
            serveRequestErrors(error, (message) => {
                formik.setFieldError("confirmPassword", message);
            });
        } finally {
            formik.setSubmitting(false);
        }
    };

    const onHandleNavigate = useCallback(() => {
        navigate(pathnames.login);
    }, [navigate]);

    const onHandleSuccessModal = useCallback(() => {
        successModalRef.current.onHandleShow();
    }, []);

    return (
        <div className="page-reset-password">
            <div className="reset-password">
                <AppAlert />

                <main className="main">
                    <div className="main__content main__content--background">
                        <Link to={pathnames.login} className="main__logo">
                            <img src={logo} alt="logo" />
                        </Link>
                    </div>

                    <div className="main__content main__content--form">
                        <form className="main__form" onSubmit={formik.handleSubmit}>
                            {/* <img className="main__hero" src={hero} alt="hero" /> */}
                            <h1 className="main__title">Reset your password</h1>

                            {/* prettier-ignore */}
                            <AppInput type="password" name="password" label="Enter Password" placeholder="Password" value={formik.values.password} error={formik.errors.password} touched={formik.touched.password} onChange={formik.handleChange} />

                            <ul className="main__rules">
                                {rules.map((rule, idx) => (
                                    <li key={idx} className="main__rules__maps">
                                        {rule.isValid ? (
                                            <img src={activateIcon} alt="activateIcon" className="main__rules__maps__icon" />
                                        ) : (
                                            <img src={exclamationIcon} alt="exclamationIcon" className="main__rules__maps__icon" />
                                        )}
                                        <span className={rule.isValid ? "main__rules__maps__text-green" : "main__rules__maps__text-gray"}>
                                            {rule.label}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <AppInput type="password" name="confirmPassword" label="Confirm New Password" placeholder="Confirm Password" value={formik.values.confirmPassword} error={formik.errors.confirmPassword} touched={formik.touched.confirmPassword} onChange={formik.handleChange} />

                            <div className="main__button-container">
                                <AppButton type="submit" label="Reset Password" disabled={!isFormValid} />
                            </div>
                        </form>

                        <p className="main__copyright">© {new Date().getFullYear()} Altecflex.my. All Rights Reserved.</p>
                    </div>
                </main>
            </div>
            <AppSuccessfulModal ref={successModalRef} onClick={onHandleNavigate} />
        </div>
    );
};

export default PageResetPassword;
